import React from 'react';
import { Box, Tabs, Tab, Container, Button, Typography, useMediaQuery } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import { useShell } from './ShellContext';

const SURFACE = '#0A141C';
const TEAL = '#00F1A7';
const MUTED = '#93A7A3';

const CONTEXT_LABELS = {
  consolidacao: 'Consolidação',
  startfy: 'Start',
  secretaria: 'Secretaria',
  voluntariado: 'Voluntariado',
  cursos: 'Cursos',
  discipulado: 'Discipulado',
  grupos_crescimento: 'GCs',
  jornada: 'Jornada',
};

function parseRole() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1])).tipo;
  } catch {
    return null;
  }
}

function getNavItems(context, role) {
  const isAdmin = role === 'admin' || role === 'super_admin' || role === 'admin_tenant';

  if (context === 'consolidacao') {
    const items = [
      { label: 'Meus Visitantes', path: '/area-consolidador' },
      { label: 'Capturar Card', path: '/capturar-card' },
      { label: 'Relatórios', path: '/relatorios-consolidacao' },
    ];
    if (isAdmin) {
      items.splice(2, 0,
        { label: 'Gestão de Visitantes', path: '/gestao-visitantes' },
        { label: 'Consolidadores', path: '/gestao-consolidadores' }
      );
    }
    return items;
  }

  if (context === 'startfy') {
    const items = [
      { label: 'Minha Área', path: '/area-professor' },
      { label: 'Calendário', path: '/calendario-aulas' },
    ];
    if (isAdmin) {
      items.splice(1, 0,
        { label: 'Turmas', path: '/turmas' },
        { label: 'Aulas', path: '/aulas' },
        { label: 'Frequências', path: '/frequencias' },
        { label: 'Alunos', path: '/alunos' },
        { label: 'Formandos', path: '/formandos' }
      );
    }
    return items;
  }

  if (context === 'secretaria') {
    return [
      { label: 'Visão Geral', path: '/secretaria' },
      { label: 'Migração de Alunos', path: '/migracao-alunos' },
      { label: 'Formandos', path: '/formandos' },
    ];
  }

  if (context === 'voluntariado') {
    const items = [
      { label: 'Visão Geral', path: '/voluntariado' },
    ];
    if (isAdmin) {
      items.push({ label: 'Gestão', path: '/gestao-voluntariado' });
    }
    return items;
  }

  if (context === 'cursos') {
    return [
      { label: 'Cursos', path: '/cursos' },
      { label: 'Novo Curso', path: '/cursos/novo' },
      { label: 'Instrutores', path: '/cursos/instrutores' },
      { label: 'Dashboard', path: '/cursos/dashboard' },
    ];
  }

  if (context === 'discipulado') {
    return [
      { label: 'Discipulados', path: '/discipulados' },
      { label: 'Sem Discipulador', path: '/discipulados?status=' },
      ...(isAdmin ? [{ label: 'Novo', path: '/discipulados/novo' }] : []),
    ];
  }

  if (context === 'grupos_crescimento') {
    return [
      { label: 'GCs', path: '/grupos-crescimento' },
      { label: 'Dashboard', path: '/grupos-crescimento/dashboard' },
      ...(isAdmin ? [{ label: 'Novo GC', path: '/grupos-crescimento/novo' }] : []),
    ];
  }

  if (context === 'jornada') {
    return [
      { label: 'Membros', path: '/jornada' },
      { label: 'Alertas', path: '/jornada/alertas' },
    ];
  }

  return [];
}

/**
 * Barra de navegação contextual — padrão único (dark + teal), sem cores por módulo.
 * Exibe breadcrumb "Início › Contexto" e tabs de sub-páginas do contexto.
 */
export default function ContextNavBar({ context }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));

  const inShell = useShell();
  if (inShell) return null;

  const label = CONTEXT_LABELS[context] || context;
  const role = parseRole();
  const navItems = getNavItems(context, role);
  const activeIdx = navItems.findIndex(item => pathname === item.path);

  return (
    <Box sx={{ bgcolor: SURFACE, borderBottom: `1px solid ${'rgba(0,241,167,0.12)'}`, boxShadow: 0 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 1.2 }}>
          <Button
            size="small"
            startIcon={<HomeIcon sx={{ fontSize: 15 }} />}
            onClick={() => navigate('/home')}
            sx={{
              color: MUTED,
              px: 0.5,
              minWidth: 0,
              fontWeight: 600,
              fontSize: '0.8rem',
              textTransform: 'none',
              '&:hover': { color: '#E9F1EF', bgcolor: 'rgba(255,255,255,0.05)' },
            }}
          >
            Início
          </Button>
          <Typography variant="caption" sx={{ color: '#5E6E6A', lineHeight: 1 }}>/</Typography>
          <Typography variant="caption" sx={{ color: '#E9F1EF', fontWeight: 700 }}>{label}</Typography>
        </Box>

        {navItems.length > 0 && (
          <Tabs
            value={activeIdx >= 0 ? activeIdx : false}
            onChange={(_, v) => navigate(navItems[v].path)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{ sx: { bgcolor: TEAL, height: 2, borderRadius: 2 } }}
            sx={{
              mt: 0.5,
              minHeight: 42,
              alignItems: 'flex-end',
              '& .MuiTabs-flexContainer': { alignItems: 'flex-end' },
              '& .MuiTab-root': {
                color: MUTED,
                fontWeight: 600,
                fontSize: '0.85rem',
                textTransform: 'none',
                minHeight: 38,
                py: 0.5,
                px: { xs: 1.5, sm: 2 },
                transition: 'background 0.15s, color 0.15s',
                '&:hover': { color: '#E9F1EF', bgcolor: 'rgba(255,255,255,0.04)' },
              },
              '& .Mui-selected': {
                color: TEAL,
                fontWeight: 700,
                bgcolor: 'rgba(0,241,167,0.08)',
              },
            }}
          >
            {navItems.map((item) => (
              <Tab key={item.path} label={item.label} disableRipple={false} />
            ))}
          </Tabs>
        )}
      </Container>
    </Box>
  );
}
